class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('.predictive-search__results');
    this.overlay = this.querySelector('.predictive-search__overlay');
    this.isOpen = false;
    this.controller = new AbortController();
    this.working = false;
    this.hasPlaceholder = false;
    this.timeoutQuery;
    
    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', (event) => {
      this.open();
      this.setLiveRegionLoadingState();
      if(this.timeoutQuery) {
        clearTimeout(this.timeoutQuery);
      }
      this.timeoutQuery = setTimeout(this.onChange.bind(this), 300);
    });
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.overlay.addEventListener('click', this.onClickOverlay.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();
    if (!searchTerm.length) {
      this.close(true);
      return;
    }
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    const selectedResult = this.querySelector('[aria-selected="true"] a');

    if (!this.getQuery().length || selectedResult) {
      event.preventDefault();
      return;
    }

    // Submit the same normalized model format used by predictive search so
    // the full search results page also works for K12X / K-12X / K 12 X, etc.
    const submitTerm = this._effectiveSearchTerm(this.getQuery());
    if (submitTerm) this.input.value = submitTerm;
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onClickOverlay() {
    this.close();
  }

  getSearchResults(searchTerm) {
    const queryKey = this._cacheKeyFor(searchTerm);
    
    if (!Shopify.designMode && this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    if(this.working) {
      this.controller.abort();
      this.controller = new AbortController();
    }
    const signal = this.controller.signal;
    this.working = true;

    const searchQueries = this._buildSearchQueries(searchTerm);
    let firstResultsMarkup = null;
    let bestResultsMarkup = null;

    this._fetchPredictiveSearchResults(searchQueries, signal)
      .then((results) => {
        results.forEach((resultsMarkup) => {
          if (!firstResultsMarkup) firstResultsMarkup = resultsMarkup;
          if (!bestResultsMarkup && this._hasProductResults(resultsMarkup)) {
            bestResultsMarkup = resultsMarkup;
          }
        });

        const resultsMarkupToRender = bestResultsMarkup || firstResultsMarkup;
        if (!resultsMarkupToRender) return;

        this.cachedResults[queryKey] = resultsMarkupToRender;
        this.renderSearchResults(resultsMarkupToRender);
      })
      .catch((error) => {
        // Aborted requests are expected while the customer is typing quickly.
        if (error.name === 'AbortError') return;
        this.close();
        throw error;
      })
      .finally(() => {
        this.working = false;
      });
  }

  _fetchPredictiveSearchResults(searchQueries, signal) {
    const results = [];

    // Query sequentially instead of firing many requests at once. Stop as soon as
    // a query returns actual product links. This fixes model-number searches such
    // as K12X => K-12X / K-12 while keeping the search fast.
    return searchQueries.reduce((promise, query) => {
      return promise.then((foundProductResult) => {
        if (foundProductResult) return true;

        const url = `${routes.predictive_search_url}?q=${encodeURIComponent(query)}&${encodeURIComponent('resources[limit]')}=10&section_id=predictive-search`;

        return fetch(url, { signal })
          .then((response) => {
            if (!response.ok) throw new Error(response.status);
            return response.text();
          })
          .then((text) => {
            const resultsMarkup = getDomHtmlFromText(text, '#shopify-section-predictive-search');
            results.push(resultsMarkup);
            return this._hasProductResults(resultsMarkup);
          });
      });
    }, Promise.resolve(false)).then(() => results);
  }

  _hasProductResults(resultsMarkup) {
    if (!resultsMarkup) return false;

    return !!resultsMarkup.querySelector([
      'a[href*="/products/"]',
      '[data-predictive-search-products] a',
      '.predictive-search__item[href*="/products/"]',
      '.predictive-search__list-item a[href*="/products/"]'
    ].join(','));
  }

  /**
   * Normalize a search term for caching (not for display).
   */
  _cacheKeyFor(term) {
    return (term || '')
      .toString()
      .toLowerCase()
      .trim()
      .replace(/[\u00A0]/g, ' ')
      .replace(/[\s]+/g, '-')      // all whitespace => dash
      .replace(/-+/g, '-')          // collapse
      .replace(/[^a-z0-9\-]/g, ''); // keep it stable
  }

  /**
   * Build multiple model-number search attempts so Shopify can find products
   * even when a customer omits punctuation or uses spaces:
   * K12XP, K12X, K-12XP, K-12X, K 12 X, SS16X, SS-16X, etc.
   */
  _buildSearchQueries(term) {
    const raw = (term || '').toString().trim().replace(/[\u00A0]/g, ' ');
    const compact = this._compactSearchTerm(raw);
    const effective = this._effectiveSearchTerm(raw);
    const model = this._parseModelNumber(raw);
    const queries = [];

    const add = (value) => {
      const q = (value || '').toString().replace(/\s+/g, ' ').trim();
      if (q && !queries.some((existing) => existing.toLowerCase() === q.toLowerCase())) {
        queries.push(q);
      }
    };

    add(raw);
    add(effective);
    add(compact);

    if (model) {
      if (model.type === 'letters-first') {
        // K12X -> K-12X, K 12X, K 12 X
        add(`${model.prefix}-${model.number}${model.suffix}`);
        add(`${model.prefix} ${model.number}${model.suffix}`);
        if (model.suffix) add(`${model.prefix} ${model.number} ${model.suffix}`);

        // If the customer typed a partial suffix (K12X), also try the base model
        // (K-12 / K12). Shopify will then return K-12XP and other close variants.
        if (model.suffix) {
          add(`${model.prefix}-${model.number}`);
          add(`${model.prefix}${model.number}`);
          add(`${model.prefix} ${model.number}`);
        }
      } else if (model.type === 'numbers-first') {
        // 385C2 -> 385-C2, 385 C2
        add(`${model.prefix}-${model.number}${model.suffix}`);
        add(`${model.prefix} ${model.number}${model.suffix}`);
        if (model.suffix) add(`${model.prefix}-${model.number}`);
      }
    }

    // Last fallback: previous spaced behavior, useful for non-model searches.
    add(raw.replace(/[_\-]+/g, ' ').replace(/([a-zA-Z])([0-9])/g, '$1 $2').replace(/([0-9])([a-zA-Z])/g, '$1 $2'));

    return queries.slice(0, 8);
  }

  _compactSearchTerm(term) {
    return (term || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  _parseModelNumber(term) {
    const compact = this._compactSearchTerm(term);
    if (!compact) return null;

    let match = compact.match(/^([a-z]+)(\d+)([a-z0-9]*)$/i);
    if (match) {
      return {
        type: 'letters-first',
        prefix: match[1].toUpperCase(),
        number: match[2],
        suffix: (match[3] || '').toUpperCase()
      };
    }

    match = compact.match(/^(\d+)([a-z]+)([a-z0-9]*)$/i);
    if (match) {
      return {
        type: 'numbers-first',
        prefix: match[1],
        number: match[2].toUpperCase(),
        suffix: (match[3] || '').toUpperCase()
      };
    }

    return null;
  }

  /**
   * Build the best single search term for direct submission/search pages.
   * We keep model suffixes attached to the number (K-12X), instead of splitting
   * them into separate words (K 12 X), because Shopify's predictive search often
   * fails on split model-number suffixes.
   */
  _effectiveSearchTerm(term) {
    let t = (term || '').toString().trim().replace(/[\u00A0]/g, ' ');
    const model = this._parseModelNumber(t);

    if (model) {
      return `${model.prefix}-${model.number}${model.suffix}`;
    }

    // For normal text searches, treat punctuation as spaces and collapse.
    t = t.replace(/[_\-]+/g, ' ');
    t = t.replace(/\s+/g, ' ').trim();
    return t;
  }


  setLiveRegionLoadingState() {
    this.setAttribute('loading', true);
    if(!this.hasPlaceholder) {
      const temp = document.getElementById("predictive-search-placeholder-tpl");
      const placeholder = temp.content.cloneNode(true);
      this.querySelector('.predictive-search__loading-state').append(placeholder);
      this.hasPlaceholder = true;
    }
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup.innerHTML;

    this.predictiveSearchResults.querySelectorAll('script').forEach(oldScriptTag => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach(attribute => {
        newScriptTag.setAttribute(attribute.name, attribute.value)
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });

    this.setAttribute('results', true);

    this.setLiveRegionResults();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
  }

  open() {
    if(!this.isOpen) {
      if(!this.classList.contains('run-top')) {
        if(!this.timeoutRunTop) {
          this.timeoutRunTop = setTimeout(() => {
            this.classList.add('run-top');
            this.timeoutRunTop = null;
          }, 500);
        }
      }
      
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
    }
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    
    this.classList.remove('run-top');

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
   
    this.isOpen = false;
    if(this.nextElementSibling && this.nextElementSibling.classList.contains('search-modal__close-button')) {
      this.nextElementSibling.dispatchEvent(new Event('click'));
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);// JavaScript Document